// Unit-word and time-expression utilities. Pure functions, no I/O.

export const NUMBER_WORDS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

// Speech-to-text mishearings of number words, accepted ONLY in the time
// slot of a trailing time expression ("… or units" is "four units" with
// the f dropped — a real UK-dictation failure observed in production).
// Never used for dates, pages or other number positions: "page or" must
// stay unparsed, but a mis-typed/mis-heard number directly before
// "unit(s)" is always a number.
const TIME_SLOT_HOMOPHONES: Record<string, number> = {
  a: 1,
  an: 1,
  won: 1,
  to: 2,
  too: 2,
  free: 3,
  tree: 3,
  or: 4,
  for: 4,
  fore: 4,
  severn: 7,
  ate: 8,
  oh: 0,
  nought: 0,
};

/**
 * Parse a number for the time slot: normal digit/word forms first, then
 * homophone-tolerant single tokens and tens+ones compounds
 * ("twenty or" -> 24).
 */
export function parseTimeSlotNumber(candidate: string): number | null {
  const trimmed = candidate.trim();
  if (!trimmed) return null;
  const straight = parseWholeNumber(trimmed);
  if (straight && straight.len === trimmed.length) return straight.value;
  const tokens = trimmed.toLowerCase().split(/[\s-]+/).filter((t) => t.length > 0);
  const values = tokens.map((t) => NUMBER_WORDS[t] ?? TIME_SLOT_HOMOPHONES[t]);
  if (values.some((v) => v === undefined)) return null;
  if (values.length === 1) return values[0]!;
  if (
    values.length === 2 &&
    values[0]! % 10 === 0 &&
    values[0]! >= 20 &&
    values[0]! <= 90 &&
    values[1]! >= 1 &&
    values[1]! <= 9
  ) {
    return values[0]! + values[1]!;
  }
  return null;
}

/**
 * Parse a leading whole-number expression in either digit or word form.
 * Returns the numeric value plus the matched character length.
 * Examples: "12" -> {value:12,len:2}; "twenty four" -> {value:24,len:11}
 *           "one" -> {value:1,len:3}; "twenty" -> {value:20,len:6}
 */
export function parseWholeNumber(input: string): { value: number; len: number } | null {
  const s = input.trimStart();
  const lead = input.length - s.length;
  // Digits first.
  const digitMatch = /^(\d+)/.exec(s);
  if (digitMatch) {
    return { value: Number(digitMatch[1]), len: lead + (digitMatch[1] ?? '').length };
  }
  // Word numbers — single, then compound (twenty four, thirty one, ...).
  const wordMatch = /^([a-z]+)(?:[\s-]([a-z]+))?/i.exec(s);
  if (!wordMatch) return null;
  const w1 = wordMatch[1]?.toLowerCase();
  if (w1 === undefined) return null;
  const v1 = NUMBER_WORDS[w1];
  if (v1 === undefined) return null;
  // Compound: tens + ones (e.g., twenty four). Only valid when v1 in {20,30,...,90} and v2 in 1..9.
  if (wordMatch[2] && v1 % 10 === 0 && v1 >= 20 && v1 <= 90) {
    const w2 = wordMatch[2].toLowerCase();
    const v2 = NUMBER_WORDS[w2];
    if (v2 !== undefined && v2 >= 1 && v2 <= 9) {
      return { value: v1 + v2, len: lead + wordMatch[0].length };
    }
  }
  return { value: v1, len: lead + (wordMatch[1]?.length ?? 0) };
}

/**
 * Convert "twenty twenty four" / "two thousand and twenty four" / "1984" / "24" into a 4-digit year.
 * Heuristics:
 *   - bare digits 1900..2099 -> as-is
 *   - bare digits 0..99 -> 2000 + n  (so "24" -> 2024)
 *   - "twenty twenty four" -> 2024
 *   - "twenty twenty" -> 2020
 *   - "two thousand and twenty four" / "two thousand twenty four" -> 2024
 *   - "nineteen eighty four" -> 1984
 */
export function parseYear(input: string): { value: number; len: number } | null {
  const s = input.trimStart();
  const lead = input.length - s.length;
  const digitMatch = /^(\d{1,4})/.exec(s);
  if (digitMatch) {
    const n = Number(digitMatch[1]);
    if (digitMatch[1]!.length === 4) return { value: n, len: lead + 4 };
    if (digitMatch[1]!.length === 2) return { value: 2000 + n, len: lead + 2 };
    return null;
  }
  // "two thousand [and] N"
  const twoThousand = /^two\s+thousand(?:\s+and)?(?:\s+(.+))?$/i.exec(s);
  if (twoThousand) {
    if (!twoThousand[1]) return { value: 2000, len: lead + 'two thousand'.length };
    const rest = parseWholeNumber(twoThousand[1]);
    if (rest && rest.value >= 0 && rest.value <= 99) {
      return { value: 2000 + rest.value, len: s.length + lead };
    }
    return { value: 2000, len: lead + 'two thousand'.length };
  }
  // "twenty twenty four" / "twenty twenty" / "nineteen eighty four".
  // Separators accept hyphens too: speech-to-text renders compound years
  // as "twenty twenty-six" / "nineteen ninety-nine".
  const words = s.toLowerCase().match(/^([a-z]+)(?:[\s-]+([a-z]+))?(?:[\s-]+([a-z]+))?/);
  if (words) {
    const [full, w1, w2, w3] = words;
    if (!w1 || !w2) return null;
    const v1 = NUMBER_WORDS[w1];
    if (v1 === undefined) return null;
    // Two-word centuries: "twenty twenty" or "nineteen eighty"
    if ((v1 === 19 || v1 === 20) && w2) {
      const v2 = NUMBER_WORDS[w2];
      if (v2 === undefined) return null;
      let yearTens = 0;
      let consumed = (w1?.length ?? 0) + 1 + (w2?.length ?? 0);
      if (v2 % 10 === 0 && v2 >= 20 && v2 <= 90) {
        // e.g. "nineteen eighty"
        if (w3) {
          const v3 = NUMBER_WORDS[w3];
          if (v3 !== undefined && v3 >= 1 && v3 <= 9) {
            yearTens = v2 + v3;
            consumed += 1 + (w3?.length ?? 0);
          } else {
            yearTens = v2;
          }
        } else {
          yearTens = v2;
        }
      } else if (v2 >= 0 && v2 <= 19) {
        // e.g. "twenty twenty" / "twenty four" — but interpret as full year
        yearTens = v2;
      } else {
        return null;
      }
      const year = v1 * 100 + yearTens;
      return { value: year, len: lead + consumed };
    }
  }
  return null;
}

/** Convert N units (whole) to decimal time using unitToDecimal (default 0.1 each). */
export function unitsToDecimal(units: number, unitToDecimal: number): number {
  // Round to 1 dp to avoid 0.1 * 7 = 0.7000000000000001
  return Math.round(units * unitToDecimal * 10) / 10;
}

/** Inverse of unitsToDecimal. */
export function decimalToUnits(decimal: number, unitToDecimal: number): number {
  if (unitToDecimal === 0) return 0;
  return Math.round(decimal / unitToDecimal);
}

/** Strict full-token whole number ("two", "twenty five", "12") or null. */
function strictWhole(s: string): number | null {
  const t = s.trim();
  const p = parseWholeNumber(t);
  return p && p.len === t.length ? p.value : null;
}

/**
 * Trailing strict number before an "hours" suffix: digits ("1.2", "2"),
 * point form ("one point five", "point five"), or number words ("two",
 * "twenty five"). Deliberately no homophone tolerance — "for hours" is
 * ordinary English, not "4 hours".
 */
function parseStrictHoursNumber(text: string): { v: number; start: number } | null {
  const t = text.trimEnd();
  const dig = /(?:^|[\s,])(\d+(?:\.\d+)?)$/.exec(t);
  if (dig && dig.index !== undefined) {
    const v = Number(dig[1]);
    if (Number.isFinite(v)) {
      const start = dig.index + (dig[0]!.startsWith(' ') || dig[0]!.startsWith(',') ? 1 : 0);
      return { v, start };
    }
  }
  const pm = /(?:(\S+)\s+)?point\s+(\S+)$/i.exec(t);
  if (pm && pm.index !== undefined) {
    const fracTok = pm[2]!;
    const frac = /^\d$/.test(fracTok) ? Number(fracTok) : strictWhole(fracTok);
    if (frac !== null && frac >= 0 && frac <= 9) {
      let whole = 0;
      let start = pm.index + (pm[0]!.startsWith(' ') ? 1 : 0);
      if (pm[1]) {
        const w = strictWhole(pm[1]!);
        if (w !== null) {
          whole = w;
          start = pm.index;
        } else {
          const bare = /(?:^|\s)(point\s+\S+)$/i.exec(t);
          if (!bare || bare.index === undefined) return null;
          start = bare.index + (bare[0]!.startsWith(' ') ? 1 : 0);
        }
      }
      return { v: whole + frac / 10, start };
    }
  }
  const tokens = t.split(/\s+/);
  for (let take = Math.min(3, tokens.length); take >= 1; take -= 1) {
    const cand = tokens.slice(tokens.length - take).join(' ');
    const w = strictWhole(cand.replace(/^,\s*/, '').replace(/,\s*$/, ''));
    if (w !== null) {
      return { v: w, start: Math.max(0, t.length - cand.length) };
    }
  }
  return null;
}

export interface TimeMatch {
  units: number;
  decimalTime: number;
  matchedText: string;
  /** Index in the input string where the time expression starts. */
  startIndex: number;
}

/**
 * Find a TRAILING time expression in `text`. Returns null if there is no time
 * expression, which is the signal "do not create a row".
 *
 * Recognised forms (at the very end, optionally preceded by a comma):
 *   - "<N> unit" / "<N> units"    where N is a digit or number-word
 *   - "<N>"                        where N is a digit followed by 'unit'/'units'
 *   - "0.X" / ".X"                 decimal in 0..9.9
 *   - "zero point <X>" / "point <X>"  word-form decimal
 *   - "<N> point <X>"              e.g. "one point five" -> 1.5
 *
 * Bare numbers without 'unit'/'units' or 'point' are NOT treated as a time
 * expression — they are too ambiguous (e.g., "review section 24").
 */
export function findTrailingTime(text: string, unitToDecimal: number): TimeMatch | null {
  const trimmed = text.replace(/[\s.,;:!?]+$/u, '');
  if (!trimmed) return null;

  // 1) "<N> unit(s)" — digits. Only integer N is accepted in this form, so
  // the `units` and `decimalTime` columns can never disagree. Fractional
  // values must use the "0.X" or "point X" forms below.
  const digUnit = /(?:^|[\s,])(\d+)\s+units?$/i.exec(trimmed);
  if (digUnit && digUnit.index !== undefined) {
    const n = Number(digUnit[1]);
    if (Number.isInteger(n)) {
      const start = digUnit.index + (digUnit[0]!.startsWith(' ') || digUnit[0]!.startsWith(',') ? 1 : 0);
      return {
        units: n,
        decimalTime: unitsToDecimal(n, unitToDecimal),
        matchedText: trimmed.slice(start),
        startIndex: start,
      };
    }
  }

  // 2) "<word-N> unit(s)" — word number (one..twenty or compound)
  // We need to back-match. Try progressively longer trailing word sequences.
  const unitWordMatch = /\bunits?$/i.exec(trimmed);
  if (unitWordMatch && unitWordMatch.index !== undefined) {
    const beforeUnits = trimmed.slice(0, unitWordMatch.index).trimEnd();
    // Walk back up to 3 words, try parseWholeNumber on the trailing 1..3 words.
    const wordsBefore = beforeUnits.match(/(\S+)\s*$/);
    if (wordsBefore) {
      // Try last 1..3 tokens
      const tokens = beforeUnits.split(/\s+/);
      for (let take = Math.min(3, tokens.length); take >= 1; take--) {
        const candidate = tokens.slice(tokens.length - take).join(' ');
        // "one point two units" must fall through to the point-form
        // branch below — matching just "two units" here would turn a
        // decimal 1.2 into 2 whole units.
        const preceding = tokens[tokens.length - take - 1];
        if (preceding && preceding.toLowerCase().replace(/[^a-z]/g, '') === 'point') continue;
        // Strip a trailing comma if any
        const cleanCandidate = candidate.replace(/^,?\s*/, '').replace(/,\s*$/, '');
        const units = parseTimeSlotNumber(cleanCandidate);
        if (units !== null) {
          const startInTrimmed = trimmed.length - (unitWordMatch[0]!.length + 1 + candidate.length);
          return {
            units,
            decimalTime: unitsToDecimal(units, unitToDecimal),
            matchedText: trimmed.slice(Math.max(0, startInTrimmed)),
            startIndex: Math.max(0, startInTrimmed),
          };
        }
      }
    }
  }

  // "<number> minutes" — converted to units at unitToDecimal*60 minutes
  // per unit (6 at the default), rounding partial units UP per billing
  // convention: "48 minutes" -> 8 units / 0.8, "20 minutes" -> 4 units.
  // Strict INTEGER numbers only — "reviewing the minutes" (of a
  // meeting) and "discussed for minutes" are prose, never time.
  const minutesSuffix = /\s+min(?:ute)?s?$/i.exec(trimmed);
  if (minutesSuffix && minutesSuffix.index !== undefined) {
    const mcore = trimmed.slice(0, minutesSuffix.index);
    const value = parseStrictHoursNumber(mcore);
    if (
      value !== null &&
      Number.isInteger(value.v) &&
      value.v > 0 &&
      value.v <= 600
    ) {
      const minutesPerUnit = unitToDecimal * 60;
      const units = Math.max(1, Math.ceil(value.v / minutesPerUnit - 1e-9));
      return {
        units,
        decimalTime: unitsToDecimal(units, unitToDecimal),
        matchedText: trimmed.slice(value.start),
        startIndex: value.start,
      };
    }
    return null; // "minutes" with no clean whole number stays unparsed
  }

  // "<number> hours" — the firm's convention is integers with units,
  // decimals with hours. Strict number parsing only: "discussed the
  // matter for hours" is ordinary English, so the time-slot homophone
  // map must NOT apply here.
  const hoursSuffix = /\s+hours?$/i.exec(trimmed);
  if (hoursSuffix && hoursSuffix.index !== undefined) {
    const hcore = trimmed.slice(0, hoursSuffix.index);
    const value = parseStrictHoursNumber(hcore);
    if (value !== null && value.v > 0 && value.v < 100) {
      const decimalTime = Math.round(value.v * 100) / 100;
      const units = decimalToUnits(decimalTime, unitToDecimal);
      return {
        units,
        decimalTime,
        matchedText: trimmed.slice(value.start),
        startIndex: value.start,
      };
    }
    return null; // "hours" with no clean number stays unparsed
  }

  // Decimal-time forms may carry a redundant trailing "units" word:
  // "1.2 units" / "one point two units" means decimal time 1.2 (twelve
  // units), matching the bare "0.2" convention — dictated exactly that
  // way in production (Maisie, 2026-07-20). Strip the word for branches
  // 3 and 4; integer forms were handled above.
  const unitsSuffix = /\s+units?$/i.exec(trimmed);
  const core = unitsSuffix ? trimmed.slice(0, unitsSuffix.index) : trimmed;

  // 3) Trailing "0.X" or ".X" decimal — direct decimal time.
  const decMatch = /(?:^|[\s,])(\d*\.\d+)$/.exec(core);
  if (decMatch && decMatch.index !== undefined) {
    const dec = Number(decMatch[1]);
    if (Number.isFinite(dec) && dec > 0 && dec < 100) {
      const start = decMatch.index + (decMatch[0]!.startsWith(' ') || decMatch[0]!.startsWith(',') ? 1 : 0);
      const decimalTime = Math.round(dec * 100) / 100;
      const units = decimalToUnits(decimalTime, unitToDecimal);
      return { units, decimalTime, matchedText: trimmed.slice(start), startIndex: start };
    }
  }

  // 4) Trailing "[zero|one|two|...] point <digit-or-word>"
  // Examples: "zero point two", "point two", "one point five"
  const pointWordMatch = /(?:(\S+)\s+)?point\s+(\S+)$/i.exec(core);
  if (pointWordMatch && pointWordMatch.index !== undefined) {
    const wholePart = pointWordMatch[1]; // may be undefined ("point two")
    const fracPart = pointWordMatch[2]!;
    let whole = 0;
    let startIdx = pointWordMatch.index;
    if (wholePart) {
      const w = parseTimeSlotNumber(wholePart);
      if (w === null) {
        // wholePart might be part of the description — re-match without it
        const bareMatch = /(?:^|\s)point\s+(\S+)$/i.exec(core);
        if (bareMatch && bareMatch.index !== undefined) {
          const digFp = /^\d+$/.test(bareMatch[1]!) ? Number(bareMatch[1]) : null;
          const frac = digFp ?? parseTimeSlotNumber(bareMatch[1]!) ?? undefined;
          if (frac !== undefined && frac >= 0 && frac <= 9) {
            const decimalTime = Math.round((0 + frac / 10) * 100) / 100;
            const units = decimalToUnits(decimalTime, unitToDecimal);
            const start = bareMatch.index + (bareMatch[0]!.startsWith(' ') ? 1 : 0);
            return { units, decimalTime, matchedText: trimmed.slice(start), startIndex: start };
          }
        }
        return null;
      }
      whole = w;
    } else {
      startIdx = pointWordMatch.index + (pointWordMatch[0]!.startsWith(' ') ? 1 : 0);
    }
    let frac: number | null = null;
    if (/^\d+$/.test(fracPart)) frac = Number(fracPart);
    else frac = parseTimeSlotNumber(fracPart);
    if (frac === null || frac < 0 || frac > 9) return null;
    const decimalTime = Math.round((whole + frac / 10) * 100) / 100;
    const units = decimalToUnits(decimalTime, unitToDecimal);
    return { units, decimalTime, matchedText: trimmed.slice(startIdx), startIndex: startIdx };
  }

  return null;
}
